// src/pages/merchant-pages/AddFullNoCustomerContainer.js
import { useState, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";


// Styles
import classes from "../../components/Modal.module.css";

// Utils
import { DataContext } from "../../utils/DataContext";
import { UserAuth } from "../../utils/AuthContext";
import { onlyNums } from "../../utils/OnlyNums";
import { formatDate } from "../../utils/toDate";
import { shouldCloseModal } from "../../utils/shouldCloseModal";

// Components
import ErrorBox from "../../components/ErrorBox";
import Loader from "../../components/Loader";
import CustomInput from "../../components/CustomInput";
import Dropdown from "../../components/Dropdown";
import CustomButton from "../../components/CustomButton";
import PDFUploader from "../../components/PDFUploader";
import Modal from "../../components/Modal";
import AreYouSureModal from "../../components/AreYouSureModal";

const AddFullNoCustomerContainer = ({ setShowAddContainerModal }) => {
  const { t } = useTranslation("general");

  const {
    selectedSupplier,
    error,
    setError,
    setShippedContainers,
    setNetworkError,
    setShippedContainersCount,
    subCompanies,
    selectedSubCompany,
    handleSelectSubCompany,
    getSubCompanies,
  } = useContext(DataContext);

  const { userData } = useContext(UserAuth);
  const pdfUploaderRef = useRef(null);

  const [loadingModal, setLoadingModal] = useState(false);
  const [invoiceDocs, setInvoiceDocs] = useState([]);

  const [showAreYouSure, setShowAreYouSure] = useState(false);

  const [page, setPage] = useState(1);
  const [inputs, setInputs] = useState([
    {
      containerNumber: "",
      createdBy: "",
      expectedArrivalDate: "",
      finalDestination: "",
      shipper: "",
      material: "",
      numberOfBoxes: 0,
      measuringUnit: "",
      unitPrice: 0,
      exitDate: "",
      dateOfEntry: new Date(),
      shippingDate: "",
      permitsReceivingDate: "",
      amountForSupplier: "",
      abstractAmountForCustomer: "",
      finalAmountForCustomer: "",
      notes: "",
      // delivered: "",
    },
  ]);

  const [delivered, setDelivered] = useState({
    title: "notDelivered",
    value: false,
  });

  const handleInputChange = (e, index) => {
    const { name, value } = e.target;
    // Convert 'received' and 'finalAmountForCustomer' to Float before setting them in the state
    let updatedValue;
    if (value) {
      updatedValue =
        name === "finalAmountForCustomer" ? parseFloat(value) : value;
    } else {
      updatedValue = value;
    }

    const updatedInputs = [...inputs];
    updatedInputs[index][name] = updatedValue;
    setInputs(updatedInputs);
  };

  const handleChangeOption = (option) => {
    setDelivered(option);
  };

  const handleDateChange = (e) => {
    const date = e.target.value;
    const name = e.target?.name;

    const updatedInputs = [...inputs];
    updatedInputs[page - 1][name] = new Date(date);
    setInputs(updatedInputs);
  };

  const handleAddRow = async () => {
    try {
      const token = userData?.token;
      const userId = parseInt(userData.userId, 10);

      const organizationId = parseInt(
        localStorage.getItem("organizationId"),
        10
      );

      if (!userId) {
        console.error("User ID not found in localStorage");
        return;
      }

      setLoadingModal(true);

      const oneContainer = inputs?.[0];
      oneContainer.delivered = delivered;

      // Ensure that date fields are in the correct format
      const dateFields = {
        expectedArrivalDate: inputs[0]?.expectedArrivalDate,
        exitDate: inputs[0]?.exitDate,
        dateOfEntry: inputs[0]?.dateOfEntry?.toISOString().split("T")[0],
        shippingDate: inputs[0]?.shippingDate,
        permitsReceivingDate: inputs[0]?.permitsReceivingDate,
      };

      if (!inputs[0]?.containerNumber) {
        setError("invalidContainerNumber");
        return;
      }

      // Send the request to create or link invoice and container
      const newShippedContainer = await axios.post(
        `${process.env.REACT_APP_URL}/createMultiContainersInvoiceWithoutC`,
        {
          supplierId: selectedSupplier?.id,
          subCompanyId: selectedSubCompany?.id,
          organizationId,
          containers: [oneContainer],
          subCompanyName: selectedSubCompany?.name,
          createdBy: userData?.userId,
          ...dateFields,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "User-Id": userId,
          },
        }
      );

      // Handle response from the server
      if (newShippedContainer.status === 200) {
        const { containers } = newShippedContainer.data;

        // Add the new or linked shipped container to the state
        setShippedContainers((prev) => [containers[0], ...prev]);

        // Reset form data
        setInputs([
          {
            containerNumber: "",
            createdBy: "",
            expectedArrivalDate: "",
            finalDestination: "",
            shipper: "",
            material: "",
            numberOfBoxes: 0,
            measuringUnit: "",
            exitDate: "",
            dateOfEntry: new Date(),
            shippingDate: "",
            permitsReceivingDate: "",
            amountForSupplier: "",
            abstractAmountForCustomer: "",
            finalAmountForCustomer: "",
            notes: "",
          },
        ]);

        setError(""); // Clear error
        setShippedContainersCount((prev) => prev + 1);
        setShowAddContainerModal(false);

        handleChildUploadDocs(containers[0]?.id);
      } else {
        // Handle backend error
        console.error("Error from server:", newShippedContainer);
        const errorMessage = newShippedContainer.data.message;
        setError(errorMessage); // Set the error message in state to display it
        setNetworkError(true);
      }
    } catch (error) {
      console.error("Error adding new shippedContainer:", error);
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        const errMsg = error.response?.data?.message;
        if (errMsg === "alreadyLinkedToAnInvoice") {
          const containerNumber = error?.response?.data?.containerNumber;
          setError(`${t("container")} ${containerNumber} ${t(errMsg)}`);
        } else {
          setError(errMsg || "An error occurred");
        }
      } else {
        // Handle unexpected errors (network errors, etc.)
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setLoadingModal(false);
    }
  };

  const handleCancel = () => {
    setShowAddContainerModal(false);
    setError("");
    setInputs([
      {
        containerNumber: "",
        createdBy: "",
        expectedArrivalDate: "",
        finalDestination: "",
        shipper: "",
        material: "",
        numberOfBoxes: 0,
        measuringUnit: "",
        exitDate: "",
        dateOfEntry: new Date(),
        shippingDate: "",
        permitsReceivingDate: "",
        amountForSupplier: "",
        abstractAmountForCustomer: "",
        finalAmountForCustomer: "",
        notes: "",
        delivered: "",
      },
    ]);
  };

  const handleChildUploadDocs = (shippedContainerId) => {
    if (pdfUploaderRef.current) {
      pdfUploaderRef.current.handleUploadDocs(shippedContainerId);
    }
  };

  const handleAttemptCancel = () => {
    setShowAreYouSure(true);
  };

  const containerData = inputs[0];

  return (
    <>
      <Modal
        title="addContainer"
        onClose={handleAttemptCancel}
        footer={
          <>
            {loadingModal ? <Loader /> : null}
            <section className={classes.actions}>
              <CustomButton
                onClick={handleAddRow}
                title={t("confirm")}
                icon='save'
                iconSize={16}
                disabled={loadingModal}
              />
              <CustomButton
                title={t("cancel")}
                onClick={handleAttemptCancel}
                variant="blank"
                icon='close'
                iconSize={14}
              />
            </section>
          </>
        }
      >
        <div
          className={`${classes.flexableForm} ${classes.flexableFormWithPadding}`}
        >
          {error ? <ErrorBox>{t(error)}</ErrorBox> : null}

          <div className={classes.inputContainer}>
            <span>{t("company")}: </span>
            <Dropdown
              options={subCompanies}
              selected={selectedSubCompany}
              setSelected={handleSelectSubCompany}
              flex={true}
              getData={getSubCompanies}
              nameInAr={true}
            />
          </div>

          <section className={`${classes.section} ${classes.invoiceNumber}`}>
            <div className={classes.inputContainer}>
              <span>{t("containerNumber")}: </span>
              <div className={classes.invoiceNumberContainer}>
                <CustomInput
                  name="containerNumber"
                  value={inputs[page - 1]?.containerNumber}
                  onChange={(e) => handleInputChange(e, page - 1)}
                  flex={true}
                />
              </div>
            </div>
          </section>
          <div className={classes.inputContainer}>
            <span>{t("expectedArrivalDate")}: </span>
            <CustomInput
              name="expectedArrivalDate"
              type="date"
              flex={true}
              value={
                // inputs[page - 1]?.expectedArrivalDatoStringing().split("T")[0]
                formatDate(inputs[page - 1]?.expectedArrivalDate)
              }
              onChange={handleDateChange}
            />
          </div>

          <div className={classes.inputContainer}>
            <span>{t("finalDestination")}: </span>
            <CustomInput
              name="finalDestination"
              value={inputs[page - 1]?.finalDestination}
              onChange={(e) => handleInputChange(e, page - 1)}
              flex={true}
            />
          </div>

          <div className={classes.inputContainer}>
            <span>{t("material")}: </span>
            <CustomInput
              name="material"
              value={inputs[page - 1]?.material}
              onChange={(e) => handleInputChange(e, page - 1)}
              flex={true}
            />
          </div>

          <div className={classes.inputContainer}>
            <span>{t("numberOfBoxes")}: </span>
            <CustomInput
              name="numberOfBoxes"
              value={inputs[page - 1]?.numberOfBoxes || ""}
              onChange={(e) => handleInputChange(e, page - 1)}
              type="moneyV2"
              flex={true}
            />
          </div>

          <div className={classes.inputContainer}>
            <span>{t("measuringUnit")}: </span>
            <CustomInput
              name="measuringUnit"
              value={inputs[page - 1]?.measuringUnit || ""}
              onChange={(e) => handleInputChange(e, page - 1)}
              flex={true}
            />
          </div>
          {/* <div
          className={`o-row o-row--horizontal-align_space-between o-row--gap-size_400 o-row--vertical-align_center u-width_full-width`}
        >
          <div className={classes.inputContainer}>
            <span>{t("unitPrice")}: </span>
            <CustomInput
              name="unitPrice"
              value={inputs[page - 1]?.unitPrice || ""}
              // value={formData?.unitPrice || ""}
              onChange={(e) => handleInputChange(e, page - 1)}
              onKeyDown={(e) => onlyNums(e)}
              type="moneyV2"
              flex={true}
            />
          </div>
          <div>{selectedSupplier?.currency}</div>
        </div> */}

          <div className={classes.inputContainer}>
            <span>{t("shipper")}: </span>
            <CustomInput
              name="shipper"
              value={inputs[page - 1]?.shipper}
              onChange={(e) => handleInputChange(e, page - 1)}
              flex={true}
            />
          </div>

          <div className={classes.inputContainer}>
            <span>{t("dateOfEntry")}: </span>
            <CustomInput
              name="dateOfEntry"
              type="date"
              flex={true}
              value={
                inputs[page - 1]?.dateOfEntry
                  ? inputs[page - 1].dateOfEntry.toISOString().split("T")[0]
                  : ""
              }
              onChange={handleDateChange}
            />
          </div>

          <div className={classes.inputContainer}>
            <span>{t("shippingDate")}: </span>
            <CustomInput
              name="shippingDate"
              type="date"
              flex={true}
              value={formatDate(inputs[page - 1]?.shippingDate)}
              onChange={handleDateChange}
            />
          </div>

          <div className={classes.inputContainer}>
            <span>{t("exitDate")}: </span>
            <CustomInput
              name="exitDate"
              type="date"
              flex={true}
              value={formatDate(inputs[page - 1].exitDate)}
              onChange={handleDateChange}
            />
          </div>

          {/* <div className={classes.inputContainer}>
          <span>{t("abstractAmountForCustomer")}: </span>
          <div
            className={`o-row o-row--horizontal-align_space-between o-row--gap-size_400 o-row--vertical-align_center u-width_full-width`}
          >
            <CustomInput
              name="abstractAmountForCustomer"
              value={inputs[page - 1]?.abstractAmountForCustomer}
              onChange={(e) => handleInputChange(e, page - 1)}
              onKeyDown={(e) => onlyNums(e)}
              type="moneyV2"
              flex={true}
            />
            <div>{containerCustomer?.currency}</div>
          </div>
        </div> */}

          <div className={classes.inputContainer}>
            <span>{t("permitsReceivingDate")}: </span>
            <CustomInput
              name="permitsReceivingDate"
              type="date"
              flex={true}
              value={formatDate(inputs[page - 1]?.permitsReceivingDate)}
              onChange={handleDateChange}
            />
          </div>
          <div className={classes.inputContainer}>
            <span>{t("deliveryStatus")}: </span>

            <Dropdown
              options={[
                { title: "delivered", value: true },
                { title: "notDelivered", value: false },
              ]}
              selected={delivered}
              setSelected={(option) => handleChangeOption(option, "delivered")}
              flex
            />
          </div>

          <div className={classes.inputContainer}>
            <span className={classes.notesClass}>{t("notes")}: </span>
            <CustomInput
              name="notes"
              type="textarea"
              value={inputs[page - 1]?.notes || ""}
              onChange={(e) => handleInputChange(e, page - 1)}
              flex={true}
            />
          </div>

          <PDFUploader
            ref={pdfUploaderRef}
            invoiceNumber={"123"}
            invoiceDocs={invoiceDocs}
            setInvoiceDocs={setInvoiceDocs}
            mode="upload"
            documents={invoiceDocs}
            tempDocs={invoiceDocs}
            setTempDocs={setInvoiceDocs}
            invoiceType="shippedContainer"
          />
        </div>
      </Modal>
      {showAreYouSure ? (
        <AreYouSureModal
          shouldCloseModal={shouldCloseModal({
            ...containerData,
            invoiceDocs: invoiceDocs?.length,
            // Expect the below from Check
            dateOfEntry: "",
          })}
          onClose={() => setShowAreYouSure(false)}
          onConfirm={handleCancel}
          confirmIconType="whiteCheck"

        />
      ) : null}
    </>
  );
};

export default AddFullNoCustomerContainer;
